import { floorToMinute, toTimeInputValue } from './entryTime';
import { intervalOf, occupiedIntervals, timersHoldingTime } from './entryOverlap';

/**
 * Advisory "this entry looks wrong" reasons for a timesheet row. A hint is a presentation
 * aid and never a precondition: any own human entry can be deleted whatever its hints, and
 * another tab or an agent can make an entry invalid without this list knowing, which is why
 * the server owns the real rules.
 *
 * @typedef {{key: 'needs_review'|'overlap'|'future'|'no_description', text: string}} Hint
 */

/**
 * The hints of every human entry in a list, by entry id.
 *
 * - `needs_review`: the server flagged it (over 8 hours, or stopped by the reaper).
 * - `overlap`: its interval overlaps another live entry or a running timer. This is
 *   `occupiedIntervals`, so a superseded original (`corrected`) and an agent entry are
 *   never a conflict, on the same floored-minute basis the move check uses.
 * - `future`: it ends after the current minute.
 * - `no_description`: its description is blank.
 *
 * Agent entries and superseded originals get none.
 *
 * @param {object[]} entries fn_my_timesheet entries.
 * @param {{running?: object|null, shell?: object|null, now: number}} context `running` is
 *   the timesheet's own timer and `shell` the shell's; the shell's counts only until the
 *   list has caught up with it (see timersHoldingTime).
 * @returns {Map<string, Hint[]>} Only entries with at least one hint are present.
 */
export function entryHints(entries, { running = null, shell = null, now }) {
  const timers = timersHoldingTime({ running, shell, entries });
  const occupied = occupiedIntervals(entries, { running: timers, now });
  const currentMinute = floorToMinute(now);
  const hints = new Map();

  for (const entry of entries || []) {
    if (!entry || entry.id == null || entry.actor_type === 'agent' || entry.corrected === true) continue;
    const found = [];

    if (entry.needs_review === true) found.push({ key: 'needs_review', text: 'Needs review' });

    const interval = intervalOf(entry);
    if (interval) {
      const conflict = occupied.find((o) => o.entry !== entry && o.start < interval.end && interval.start < o.end);
      if (conflict) {
        const range = `${toTimeInputValue(conflict.start)}–${toTimeInputValue(conflict.end)}`;
        found.push({ key: 'overlap', text: `Overlaps ${range}` });
      }
      // Whether `fn_timer_stop` rounds a duration up is unconfirmed (§5f.4 #6): if it does, a
      // timer entry can end a minute after the stop and read as "future" for that minute.
      if (interval.end > currentMinute) found.push({ key: 'future', text: 'Ends in the future' });
    }

    if (!entry.what?.trim()) found.push({ key: 'no_description', text: 'No description' });

    if (found.length > 0) hints.set(entry.id, found);
  }
  return hints;
}
